/**
 * LOTE B — Editor de líneas de servicio POR OPORTUNIDAD. Una fila por línea con
 * 4 campos en una única línea: selector de línea de servicio · facturación
 * (Facturable/Probono/Pro-Equity) · recurrencia · importe (a la derecha). La
 * recurrencia y el modo de facturación son específicos de la oportunidad, no del
 * catálogo. Componente controlado: el padre posee `drafts`.
 */

import { Plus, Repeat, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  OpportunityBillingModeSchema,
  RecurringInvoicePeriodicitySchema,
  type OpportunityBillingMode,
  type OpportunityServiceLineDto,
  type OpportunityServiceLineInput,
  type RecurringInvoicePeriodicityT,
} from '@toga/shared'
import { centsToEurosInput, formatCents, parseEurosToCents } from '../../lib/money'

export interface ServiceLineDraft {
  /** Identidad estable de cliente de la fila (id de backend o uuid nuevo). Es la
   *  key de React y el índice de patch/borrar: permite repetir el mismo `code`
   *  en varias líneas (el `code` deja de ser la identidad). */
  _key: string
  code: string
  /** Importe en euros como string editable ('' = marcada sin importe). */
  valueEuros: string
  billingMode: OpportunityBillingMode
  isRecurring: boolean
  periodicity: RecurringInvoicePeriodicityT
  dayOfMonth: string
  /** Ancla de inicio: 'DATE' (fecha concreta) o una fase de ejecución. */
  recurringStartAnchor: string
  /** Inicio de facturación (YYYY-MM-DD; '' = arranca en la próxima ocurrencia). Solo si ancla = 'DATE'. */
  recurringStartDate: string
  /** Nº de cuotas ('' = indefinido / sin límite). */
  occurrences: string
}

/** Opciones de ancla de inicio del mantenimiento (fecha concreta + fases). */
export const START_ANCHORS = ['DATE', 'ONBOARDING', 'EJECUCION', 'FINALIZADO', 'SOPORTE'] as const

export function newDraft(code: string): ServiceLineDraft {
  return {
    _key: crypto.randomUUID(),
    code,
    valueEuros: '',
    billingMode: 'FACTURABLE',
    isRecurring: false,
    periodicity: 'MONTHLY',
    dayOfMonth: '1',
    recurringStartAnchor: 'DATE',
    recurringStartDate: '',
    occurrences: '',
  }
}

/** Detalle por línea del backend → drafts editables. */
export function serviceLinesToDrafts(lines: OpportunityServiceLineDto[]): ServiceLineDraft[] {
  return lines.map((l) => ({
    _key: l.id,
    code: l.code,
    valueEuros: l.valueCents > 0 ? centsToEurosInput(l.valueCents) : '',
    billingMode: l.billingMode,
    isRecurring: l.isRecurring,
    periodicity: l.periodicity ?? 'MONTHLY',
    dayOfMonth: String(l.dayOfMonth ?? 1),
    recurringStartAnchor: l.recurringStartAnchor ?? 'DATE',
    recurringStartDate: l.recurringStartDate ? l.recurringStartDate.slice(0, 10) : '',
    occurrences: l.occurrences != null ? String(l.occurrences) : '',
  }))
}

/** Drafts → payload `serviceLines` para POST/PATCH /opportunities. */
export function draftsToServiceLinesInput(
  drafts: ServiceLineDraft[]
): OpportunityServiceLineInput[] {
  return drafts.map((d) => {
    const occ = Number(d.occurrences)
    return {
      code: d.code,
      valueCents: d.valueEuros.trim() ? parseEurosToCents(d.valueEuros) : 0,
      billingMode: d.billingMode,
      isRecurring: d.isRecurring,
      periodicity: d.isRecurring ? d.periodicity : null,
      dayOfMonth: d.isRecurring ? Math.min(28, Math.max(1, Number(d.dayOfMonth) || 1)) : null,
      recurringStartAnchor: (d.isRecurring
        ? d.recurringStartAnchor
        : null) as OpportunityServiceLineInput['recurringStartAnchor'],
      // La fecha concreta solo se envía con ancla 'DATE'.
      recurringStartDate:
        d.isRecurring && d.recurringStartAnchor === 'DATE' && d.recurringStartDate.trim()
          ? new Date(`${d.recurringStartDate}T00:00:00.000Z`).toISOString()
          : null,
      occurrences:
        d.isRecurring && d.occurrences.trim() && Number.isFinite(occ) && occ > 0
          ? Math.floor(occ)
          : null,
      endsAt: null,
    }
  })
}

/** Importe de una línea en céntimos (0 si vacía). */
function draftUnitCents(d: ServiceLineDraft): number {
  return d.valueEuros.trim() ? parseEurosToCents(d.valueEuros) : 0
}

/** Nº de cuotas finito de una línea recurrente (null si indefinida o no recurrente). */
function draftOccurrences(d: ServiceLineDraft): number | null {
  if (!d.isRecurring) return null
  const n = Number(d.occurrences)
  return d.occurrences.trim() && Number.isFinite(n) && n > 0 ? Math.floor(n) : null
}

/**
 * Contribución de una línea al VALOR DE CONTRATO: recurrente con nº de cuotas
 * finito = importe × cuotas; puntual = 1 unidad; recurrente SIN fin = 0
 * (no hay contrato acotado que sumar — se señala con el sufijo "+ recurrente").
 */
export function draftContractCents(d: ServiceLineDraft): number {
  const v = draftUnitCents(d)
  const occ = draftOccurrences(d)
  if (occ != null) return v * occ
  return d.isRecurring ? 0 : v
}

/** Valor de contrato total (suma de contribuciones). */
export function draftsTotalCents(drafts: ServiceLineDraft[]): number {
  return drafts.reduce((s, d) => s + draftContractCents(d), 0)
}

/** ¿Hay alguna línea recurrente SIN fin (nº de cuotas vacío) con importe? */
export function draftsHasUnlimited(drafts: ServiceLineDraft[]): boolean {
  return drafts.some((d) => d.isRecurring && draftUnitCents(d) > 0 && draftOccurrences(d) == null)
}

interface Props {
  drafts: ServiceLineDraft[]
  onChange: (next: ServiceLineDraft[]) => void
  /** Líneas activas del catálogo (para el selector y "Añadir línea"). */
  catalogLines: { code: string; name: string }[]
  /** Nombre humano de un código (fallback para líneas retiradas). */
  nameOf: (code: string) => string
  disabled?: boolean
}

export default function ServiceLineEditor({
  drafts,
  onChange,
  catalogLines,
  nameOf,
  disabled,
}: Props) {
  const { t } = useTranslation(['opportunities', 'enums'])

  // El mismo código puede repetirse en varias líneas (p.ej. dos "Desarrollo
  // agéntico", una Facturable y otra Probono), así que el selector ofrece SIEMPRE
  // el catálogo completo, sin excluir los códigos ya usados. Fallback para una
  // línea con código retirado del catálogo (para no perderla en el <select>).
  const optionsFor = (code: string): { code: string; name: string }[] => {
    if (code && !catalogLines.some((l) => l.code === code)) {
      return [{ code, name: nameOf(code) }, ...catalogLines]
    }
    return catalogLines
  }

  const addLine = () => {
    if (catalogLines.length === 0) return
    onChange([...drafts, newDraft(catalogLines[0]!.code)])
  }
  const removeLine = (key: string) => onChange(drafts.filter((d) => d._key !== key))
  const patch = (key: string, patch: Partial<ServiceLineDraft>) => {
    onChange(drafts.map((d) => (d._key === key ? { ...d, ...patch } : d)))
  }
  const changeCode = (key: string, newCode: string) => {
    if (!newCode) return
    onChange(drafts.map((d) => (d._key === key ? { ...d, code: newCode } : d)))
  }
  // Al salir del input, reformatea con separadores de miles ("17000" → "17.000,00").
  const reformat = (key: string, raw: string) => {
    const cents = raw.trim() ? parseEurosToCents(raw) : 0
    patch(key, { valueEuros: cents ? centsToEurosInput(cents) : '' })
  }

  const inputCls =
    'px-2.5 py-1.5 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary text-sm focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60'

  return (
    <div className="space-y-2 min-w-0">
      {drafts.length === 0 && <p className="text-xs t-muted">{t('serviceLineEditor.empty')}</p>}

      {drafts.map((draft) => (
        <div
          key={draft._key}
          className="rounded-lg border border-[var(--border)] bg-[var(--bg-input-soft)] p-2 space-y-2 min-w-0"
        >
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            {/* 1) Línea de servicio (selector) */}
            <select
              disabled={disabled}
              value={draft.code}
              onChange={(e) => changeCode(draft._key, e.target.value)}
              className={`${inputCls} flex-1 min-w-[10rem] font-medium`}
            >
              {optionsFor(draft.code).map((o) => (
                <option key={o.code} value={o.code}>
                  {o.name}
                </option>
              ))}
            </select>

            {/* 2) Facturación */}
            <select
              disabled={disabled}
              value={draft.billingMode}
              onChange={(e) =>
                patch(draft._key, { billingMode: e.target.value as OpportunityBillingMode })
              }
              className={`${inputCls} w-36 shrink-0`}
              title={t('serviceLineEditor.billingMode')}
            >
              {OpportunityBillingModeSchema.options.map((m) => (
                <option key={m} value={m}>
                  {t(`enums:OpportunityBillingMode.${m}`)}
                </option>
              ))}
            </select>

            {/* 3) Recurrente (toggle) */}
            <button
              type="button"
              disabled={disabled}
              onClick={() => patch(draft._key, { isRecurring: !draft.isRecurring })}
              title={t('serviceLineEditor.recurring')}
              className={[
                'shrink-0 inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border text-xs transition',
                draft.isRecurring
                  ? 'bg-teal-500/15 border-teal-500/40 text-teal-700 dark:text-teal-300'
                  : 'border-[var(--border)] t-muted hover:bg-hover',
              ].join(' ')}
            >
              <Repeat size={13} /> {t('serviceLineEditor.recurring')}
            </button>

            {/* 4) Importe (último, a la derecha) */}
            <div className="relative w-32 shrink-0">
              <input
                type="text"
                inputMode="decimal"
                disabled={disabled}
                value={draft.valueEuros}
                onChange={(e) => patch(draft._key, { valueEuros: e.target.value })}
                onBlur={(e) => reformat(draft._key, e.target.value)}
                placeholder="0,00"
                className={`${inputCls} w-full pr-6 font-mono text-right`}
              />
              <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 t-faint text-xs">
                €
              </span>
            </div>

            {/* Quitar */}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeLine(draft._key)}
                title={t('serviceLineEditor.remove')}
                className="shrink-0 w-8 h-8 grid place-items-center rounded-lg t-muted hover:text-rose-600 dark:hover:text-rose-300 hover:bg-hover"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>

          {/* Recurrencia expandida */}
          {draft.isRecurring && (
            <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pl-1">
              <div>
                <label className="block text-[11px] t-muted mb-1">
                  {t('serviceLineEditor.periodicity')}
                </label>
                <select
                  disabled={disabled}
                  value={draft.periodicity}
                  onChange={(e) =>
                    patch(draft._key, {
                      periodicity: e.target.value as RecurringInvoicePeriodicityT,
                    })
                  }
                  className={`${inputCls} w-full`}
                >
                  {RecurringInvoicePeriodicitySchema.options.map((p) => (
                    <option key={p} value={p}>
                      {t(`enums:RecurringInvoicePeriodicity.${p}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] t-muted mb-1">
                  {t('serviceLineEditor.dayOfMonth')}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  disabled={disabled}
                  value={draft.dayOfMonth}
                  onChange={(e) => patch(draft._key, { dayOfMonth: e.target.value })}
                  className={`${inputCls} w-full`}
                />
              </div>
              <div>
                <label className="block text-[11px] t-muted mb-1">
                  {t('serviceLineEditor.occurrences')}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  disabled={disabled}
                  value={draft.occurrences}
                  onChange={(e) => patch(draft._key, { occurrences: e.target.value })}
                  placeholder={t('serviceLineEditor.occurrencesPlaceholder')}
                  className={`${inputCls} w-full`}
                />
              </div>
              <div>
                <label className="block text-[11px] t-muted mb-1">
                  {t('serviceLineEditor.startAnchor')}
                </label>
                <select
                  disabled={disabled}
                  value={draft.recurringStartAnchor}
                  onChange={(e) => patch(draft._key, { recurringStartAnchor: e.target.value })}
                  className={`${inputCls} w-full`}
                >
                  {START_ANCHORS.map((a) => (
                    <option key={a} value={a}>
                      {t(`serviceLineEditor.anchor.${a}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* Con ancla 'DATE' pedimos la fecha; con ancla por fase, avisamos. */}
            {draft.recurringStartAnchor === 'DATE' ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pl-1">
                <div className="sm:col-span-2">
                  <label className="block text-[11px] t-muted mb-1">
                    {t('serviceLineEditor.recurringStartDate')}
                  </label>
                  <input
                    type="date"
                    disabled={disabled}
                    value={draft.recurringStartDate}
                    onChange={(e) => patch(draft._key, { recurringStartDate: e.target.value })}
                    title={t('serviceLineEditor.recurringStartDateHint')}
                    className={`${inputCls} w-full`}
                  />
                </div>
              </div>
            ) : (
              <div className="pl-1 text-[11px] t-muted">
                {t('serviceLineEditor.anchorHint', {
                  phase: t(`serviceLineEditor.anchor.${draft.recurringStartAnchor}`),
                })}
              </div>
            )}
            {draftUnitCents(draft) > 0 && (
              <div className="pl-1 text-[11px] t-muted">
                {draftOccurrences(draft) != null
                  ? t('serviceLineEditor.contractLine', {
                      total: formatCents(draftContractCents(draft)),
                      count: draftOccurrences(draft),
                      unit: formatCents(draftUnitCents(draft)),
                    })
                  : t('serviceLineEditor.recurringNoEnd')}
              </div>
            )}
            </>
          )}
        </div>
      ))}

      {/* Añadir línea (una a una) */}
      {!disabled && catalogLines.length > 0 && (
        <button
          type="button"
          onClick={addLine}
          className="inline-flex items-center gap-1.5 text-sm t-secondary hover:text-toga-700 dark:hover:text-toga-200 px-2 py-1.5 rounded-lg border border-dashed border-[var(--border)] hover:bg-hover"
        >
          <Plus size={14} /> {t('serviceLineEditor.addLine')}
        </button>
      )}

      <div className="flex items-baseline justify-between border-t border-[var(--border)]/70 pt-2 text-sm">
        <span className="t-muted">{t('serviceLineEditor.total')}</span>
        <span className="font-mono t-primary">
          {formatCents(draftsTotalCents(drafts))}
          {draftsHasUnlimited(drafts) && (
            <span className="t-muted font-normal"> + {t('serviceLineEditor.recurringSuffix')}</span>
          )}
        </span>
      </div>
    </div>
  )
}
